import { useState } from "react";
import { ClipboardCheck, CheckCircle2, Circle, ScanText, ShieldCheck, Building2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { buildChecklist, checklistReady } from "@/lib/checklist";
import { labelDocument } from "@/lib/dataroom";
import { updateDocumentsRequis } from "@/lib/dossiers";
import DocumentChecklistEditor from "@/components/dossiers/DocumentChecklistEditor";
import ContexteEditor from "@/components/dossiers/ContexteEditor";
import RelanceEmailButton from "@/components/dossiers/RelanceEmailButton";

export default function ChecklistCard({ dossierId, contexteAudit, contexteAutre, onContexteChange, typeDossier, documents = [], documentsRequis, onDocumentsRequisChange, statut, onValidate, validating, holding = false, relance }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const items = buildChecklist(contexteAudit, typeDossier, documents, documentsRequis);
    const required = items.filter((i) => i.required);
    const done = required.filter((i) => i.present && i.extracted).length;
    const { ready, missing, notExtracted } = checklistReady(items);
    const alreadyReady = statut === "pret_pour_analyse" || statut === "en_cours" || statut === "clos";
    const tid = holding ? "checklist-card-holding" : "checklist-card";

    async function saveRequis(next) {
        if (holding) return;
        setSaving(true);
        try {
            await updateDocumentsRequis(dossierId, next);
            onDocumentsRequisChange?.(next);
        } catch (e) {
            toast.error("Mise à jour impossible", { description: e.message });
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card data-testid={tid} className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                            {holding ? <Building2 className="h-4 w-4 text-primary" strokeWidth={1.75} /> : <ClipboardCheck className="h-4 w-4 text-primary" strokeWidth={1.75} />}
                        </div>
                        <div>
                            <h2 className="font-display text-xl text-foreground">{holding ? "Checklist — Holding / Groupe" : "Checklist — Entité auditée"}</h2>
                            {holding ? (
                                <p className="text-xs text-muted-foreground">Mêmes règles que l&apos;entité auditée, appliquées aux documents de la holding / du groupe.</p>
                            ) : (
                                <ContexteEditor dossierId={dossierId} contexte={contexteAudit} contexteAutre={contexteAutre} onChange={onContexteChange} />
                            )}
                        </div>
                    </div>
                    <span data-testid={`${tid}-progress`} className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{done}/{required.length} obligatoires</span>
                </div>
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${required.length ? (done / required.length) * 100 : 0}%` }} />
                </div>

                {!holding && editing ? (
                    <div className="space-y-3">
                        <DocumentChecklistEditor value={required.map((i) => i.type)} onChange={saveRequis} contexteAudit={contexteAudit} typeDossier={typeDossier} documents={documents} disabled={saving} idPrefix="checklist-edit" />
                        <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="sm" data-testid="checklist-edit-done" onClick={() => setEditing(false)} className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Terminer</Button>
                            <span className="text-xs text-muted-foreground">Les changements sont enregistrés automatiquement.</span>
                        </div>
                    </div>
                ) : (
                    <ul className="space-y-2.5">
                        {items.map((i) => {
                            const ok = i.present && i.extracted;
                            return (
                                <li key={i.type} data-testid={`${tid}-item-${i.type}`} className="flex items-start gap-2 text-sm">
                                    {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> : i.present ? <ScanText className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" /> : <Circle className="h-4 w-4 mt-0.5 text-border shrink-0" />}
                                    <div className="min-w-0">
                                        <p className={ok ? "text-foreground" : "text-muted-foreground"}>
                                            {labelDocument(i.type)}
                                            <span data-testid={`${tid}-badge-${i.type}`} className={`ml-2 text-[10px] uppercase tracking-wider ${i.required ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{i.required ? "obligatoire" : "facultatif"}</span>
                                            {i.count > 0 && <span className="ml-2 text-xs text-muted-foreground">· {i.count} fichier{i.count > 1 ? "s" : ""}{!i.extracted ? " · texte non extrait" : ""}</span>}
                                        </p>
                                        <p className="text-xs text-muted-foreground/80">{i.help}</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {!holding && (
                    <div className="pt-2 border-t border-border/60 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            {!editing && (
                                <Button type="button" variant="outline" size="sm" data-testid="checklist-edit-toggle" onClick={() => setEditing(true)} className="rounded-full">
                                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Modifier les pièces requises
                                </Button>
                            )}
                            {!alreadyReady && (
                                <Button type="button" data-testid="valider-documents-button" disabled={!ready || validating} onClick={onValidate} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                                    <ShieldCheck className="h-4 w-4" strokeWidth={1.75} /> {validating ? "Validation…" : "Valider les documents — Prêt pour analyse"}
                                </Button>
                            )}
                            <RelanceEmailButton {...relance} missing={missing} notExtracted={notExtracted} disabled={missing.length === 0 && notExtracted.length === 0} />
                        </div>
                        {!ready && !alreadyReady && (
                            <p data-testid="valider-documents-hint" className="text-xs text-muted-foreground">
                                {missing.length > 0 && <>Manquant : {missing.map((m) => labelDocument(m.type)).join(", ")}. </>}
                                {notExtracted.length > 0 && <>Texte à extraire : {notExtracted.map((m) => labelDocument(m.type)).join(", ")}.</>}
                            </p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
